/**
 * The Register stage, step for step — the one list both screens read.
 *
 * <h3>Why it is here and not inside the wizard</h3>
 *
 * `ExteletRegistrationWizard.jsx` owned these privately as a local `STEPS`
 * array, which was right while it was the only surface walking a registration.
 * Protect's build trail walks the same stage, so a second list would mean the
 * two screens could disagree about what Register is — a renamed step on one and
 * not the other, an eighth step added in one place. The order, the labels, the
 * icons and where the commit falls are the shared vocabulary; what each screen
 * does with a step is its own.
 *
 * <h3>All seven steps are Register</h3>
 *
 * The commit is step 4 to 5: the Accountability button reads "Mint
 * credentials", and `goNext` submits the onboard there. The STAGE does not end
 * at the commit. Minting the keys, taking the wiring and activating are steps
 * 5, 6 and 7 of Register, and the registration row sits at
 * PENDING_VERIFICATION until its first protected call flips it to VERIFIED.
 *
 * Filing the keys under Identity — which Protect did — claims a cryptographic
 * name was stamped when what actually happened was a gateway key being handed
 * out. Identity is the SPIFFE name, its trust domain and the SVID; it is read
 * from the agent after registration, not minted during it.
 *
 * <h3>What a step is</h3>
 *
 * {@code n}      its position in the stage, as the stepper numbers it
 * {@code key}    how the build spine stores the answer, and how a screen
 *                addresses one step without counting
 * {@code label}  the step as a person reads it, in the platform's words
 * {@code icon}   the platform's own icon for that step
 */
import {
  Plug, Wand2, Code2, ShieldCheck, KeyRound, FileCode2, CheckCircle2,
} from 'lucide-react';

export const REGISTER_STEPS = [
  { n: 1, key: 'framework',      label: 'Bind framework',      icon: Plug },
  { n: 2, key: 'connect',        label: 'Connect the ExteLet', icon: Wand2 },
  { n: 3, key: 'path',           label: 'Choose path',         icon: Code2 },
  /* Accountability sits immediately BEFORE the mint. Minting is the point of no
     return — it writes the registration and hands out a working credential — so
     "who is accountable for this agent" is answered on the near side of it,
     exactly as the native Let form asks before create. */
  { n: 4, key: 'accountability', label: 'Accountability',      icon: ShieldCheck },
  { n: 5, key: 'credentials',    label: 'Mint credentials',    icon: KeyRound },
  { n: 6, key: 'wire',           label: 'Wire it up',          icon: FileCode2 },
  { n: 7, key: 'activate',       label: 'Activate',            icon: CheckCircle2 },
];

/**
 * The step the onboard is submitted on.
 *
 * Everything at or before it is still a draft the person can change and the
 * wizard can save; everything after it reports what already happened. Derived
 * from the list rather than written as a 4, so moving the step moves the commit
 * with it instead of leaving a number behind that used to be right.
 */
export const REGISTER_COMMIT_STEP =
  REGISTER_STEPS.find((s) => s.key === 'accountability').n;

/** One step by the key its answer is stored under, or null. */
export const registerStep = (key) =>
  REGISTER_STEPS.find((s) => s.key === key) || null;
